import { useEffect, useMemo, useRef, useState } from "react";
import "./DeleteModal.css";
import { Box } from "@mui/material";
import Title from "../title/Title";
import {
  DeleteButton,
  PrimaryButton,
  SecondaryButton,
} from "../buttonComponent/ButtonComponent";
import DeleteModal from "./DeleteModal";
import { useAuth } from "../../config/utilities/useAuth";
import {
  APIPatchMasterAdminInstitution,
  APIDeleteMasterAdminInstitution,
} from "../../config/utilities/APIs/masterAdmin/masterAdminApi";
import ModalCloseButton from "./ModalCloseButton";
import Institution from "./img/institution.svg";
import TextInput from "../textInput/TextInput";
import Dropdown from "../dropdown/Dropdown";

const DEFAULT_TRIP_TYPES = ["AM", "PM", "ECA"];
const DEFAULT_TRIP_TYPE_OBJECTS = [
  { id: "am", name: "AM" },
  { id: "pm", name: "PM" },
  { id: "eca", name: "ECA" },
];

const boxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  width: "min(50rem, calc(100vw - 2rem))",
  overflow: "hidden",
  maxHeight: "92vh",
};

const tripLabel = (t) => {
  if (typeof t === "string") return t;
  if (t && typeof t === "object") {
    return t.name || t.label || t.id || "";
  }
  return "";
};

const customTripTypeObjectsFromList = (tripTypes) => {
  if (!Array.isArray(tripTypes)) return [];
  const norm = (s) => String(s).trim().toUpperCase();
  let i = 0;
  const labels = tripTypes.map(tripLabel);
  if (labels[0] && norm(labels[0]) === "AM") i += 1;
  if (labels[i] && norm(labels[i]) === "PM") i += 1;
  if (labels[i] && norm(labels[i]) === "ECA") i += 1;

  return tripTypes.slice(i).map((tripType) => {
    if (tripType && typeof tripType === "object") {
      return {
        id: tripType.id || "",
        name: tripLabel(tripType) || "",
      };
    }
    return {
      id: "",
      name: tripLabel(tripType) || "",
    };
  });
};

const toTripTypeId = (name) =>
  String(name)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/\s+/g, "_");

const formatTimezoneLabel = (tz) => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeZoneName: "shortOffset",
  }).formatToParts(now);
  const offset =
    parts
      .find((p) => p.type === "timeZoneName")
      ?.value.replace("GMT", "UTC") || "UTC";
  const prettyName = tz.replace(/_/g, " ");
  return `(${offset}) ${prettyName}`;
};

const timezoneOptions = Intl.supportedValuesOf("timeZone")
  .map((tz) => ({
    label: formatTimezoneLabel(tz),
    value: tz,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

export default function EditInstitutionModal({
  isOpen,
  onClose,
  institution,
  onSave,
  onDeleted,
  onDeleteError,
  onPatchError,
  fetchInstitutions,
}) {
  const { token } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [input, setInput] = useState({
    institutionName: "",
    timezone: "",
    customTripTypes: [],
  });
  const [customTripTypeIds, setCustomTripTypeIds] = useState([]);
  const [keyUp, setKeyUp] = useState("", false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !institution) {
      setInitialSnapshot(null);
      setCustomTripTypeIds([]);
      return;
    }
    const customTripTypeObjects = customTripTypeObjectsFromList(institution.tripTypes);
    const customTripTypes = customTripTypeObjects.map((tripType) => tripType.name);
    setInput({
      institutionName: institution.institutionName || "",
      timezone: institution.timezone || "",
      customTripTypes,
    });
    setCustomTripTypeIds(customTripTypeObjects.map((tripType) => tripType.id || ""));
    setInitialSnapshot({
      institutionName: (institution.institutionName || "").trim(),
      timezone: institution.timezone || "",
      customTripTypes: customTripTypes.map((tripType) => tripType.trim()),
    });
  }, [isOpen, institution]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const checkOverflow = () => setIsOverflowing(el.scrollHeight > el.clientHeight);
    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleAddMore = () => {
    setInput((prev) => ({
      ...prev,
      customTripTypes: [...(prev.customTripTypes || []), ""],
    }));
    setCustomTripTypeIds((prev) => [...prev, ""]);
  };

  const handleCustomTripTypeChange = (index, value) => {
    setInput((prev) => {
      const nextCustomTripTypes = [...(prev.customTripTypes || [])];
      nextCustomTripTypes[index] = value;
      return { ...prev, customTripTypes: nextCustomTripTypes };
    });
  };

  const isFormValid = () => {
    const name = input.institutionName?.trim() || "";
    const tz = input.timezone?.trim() || "";
    const isAlphaNumSpace = (str) => /^[a-zA-Z0-9 ]+$/.test(str);
    const customTripTypesOk = (input.customTripTypes || []).every(
      (tripType) =>
        tripType.trim() !== "" && isAlphaNumSpace(tripType.trim()),
    );
    return (
      name !== "" &&
      name.length <= 60 &&
      isAlphaNumSpace(name) &&
      tz !== "" &&
      customTripTypesOk
    );
  };

  const customTripTypeErrors = useMemo(() => {
    const hasSpecialChars = (str) => /[^a-zA-Z0-9 ]/.test(str);
    return (input.customTripTypes || []).map((tripType) => {
      const trimmedValue = tripType.trim();
      if (trimmedValue === "") return "Trip type cannot be empty";
      if (hasSpecialChars(trimmedValue)) return "Special characters are not allowed";
      return "";
    });
  }, [input.customTripTypes]);

  const hasChanges = useMemo(() => {
    if (!initialSnapshot || !isOpen) return false;
    const name = input.institutionName?.trim() || "";
    const tz = input.timezone?.trim() || "";
    const customTripTypes = (input.customTripTypes || []).map((tripType) =>
      tripType.trim(),
    );
    return (
      name !== initialSnapshot.institutionName ||
      tz !== initialSnapshot.timezone ||
      JSON.stringify(customTripTypes) !==
        JSON.stringify(initialSnapshot.customTripTypes)
    );
  }, [initialSnapshot, input, isOpen]);

  const handleSave = async () => {
    if (!institution || !isFormValid() || !hasChanges || !token) return;
    const custom = (input.customTripTypes || [])
      .map((v) => v.trim())
      .filter(Boolean);
    const tripTypes = [
      ...DEFAULT_TRIP_TYPE_OBJECTS,
      ...custom.map((name, index) => ({
        id: customTripTypeIds[index] || toTripTypeId(name),
        name,
      })),
    ];
    const body = {
      institutionName: input.institutionName.trim(),
      timezone: input.timezone,
      tripTypes,
    };
    try {
      const res = await APIPatchMasterAdminInstitution(
        institution.institutionId,
        body,
        setSaveLoading,
        token
      );
      if (res?.status >= 200 && res?.status < 300) {
        onSave?.({
          institutionId: institution.institutionId,
          institutionName: body.institutionName,
          timezone: body.timezone,
          tripTypes,
        });
        if (fetchInstitutions) {
          await fetchInstitutions();
        }
        handleClose();
      } else {
        const msg =
          res?.response?.data?.message ||
          res?.message ||
          "Failed to update institution.";
        onPatchError?.(msg);
      }
    } catch (e) {
      onPatchError?.(e?.message || "Failed to update institution.");
    }
  };

  const handleConfirmDelete = async (item) => {
    if (!item?.institutionId || !token) return;
    if (item?._id == null || item?._id === "") {
      onDeleteError?.(
        "Missing institution document id. Try refreshing the list and try again."
      );
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await APIDeleteMasterAdminInstitution(
        item.institutionId,
        item._id,
        setDeleteLoading,
        token
      );
      if (res?.status >= 200 && res?.status < 300) {
        onDeleted?.(item.institutionId);
        setShowDeleteModal(false);
        handleClose();
      } else {
        const msg =
          res?.response?.data?.message ||
          res?.message ||
          "Failed to delete institution.";
        onDeleteError?.(msg);
        setShowDeleteModal(false);
      }
    } catch (e) {
      onDeleteError?.(e?.message || "Failed to delete institution.");
      setShowDeleteModal(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isOpen || !institution) return null;

  return (
    <>
    <div className="modal-backdrop">
      <Box sx={boxStyle}>
        <div className="user-modal-header">
          <div className="user-modal-header-contents">
            <div className="icon-button-container">
              <div className="icon-container">
                <img
                  style={{ margin: "auto" }}
                  src={Institution}
                  alt=""
                  loading="lazy"
                />
              </div>
              <div className="x-button-container">
                <ModalCloseButton
                  onClick={handleClose}
                  className="user-x-button edit-institution-close"
                />
              </div>
            </div>
            <div className="user-modal-header-title">
              <Title
                title="Edit Institution"
                subtitle="Update institution name, timezone, and trip types"
                titleSize="1rem"
                subtitleSize="0.875rem"
                gap="0.25rem"
                lineHeight="1.5rem"
              />
            </div>
          </div>
        </div>
        <div
          ref={contentRef}
          className={`user-modal-content ${
            isOverflowing ? "has-overflow" : ""
          }`}
        >
          <div className="email-container">
            <div className="user-modal-text">Institution ID</div>
            <div className="invite-text-input-container create-institution-field">
              <TextInput
                width="32rem"
                placeholder=""
                input={{ institutionId: institution.institutionId }}
                name="institutionId"
                type="text"
                disable
                onChangeFunction={() => {}}
              />
            </div>
          </div>
          <div className="email-container">
            <div className="user-modal-text">Institution Name</div>
            <div className="invite-text-input-container create-institution-field">
              <TextInput
                width="32rem"
                placeholder="Institution name"
                setInput={setInput}
                input={input}
                name="institutionName"
                type="text"
                onChangeFunction={handleInputChange}
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                showCharCount
                maxLength={60}
              />
            </div>
          </div>
          <div className="email-container" style={{ marginBottom: "1rem" }}>
            <div className="user-modal-text">Timezone</div>
            <div className="invite-text-input-container create-institution-field">
              <Dropdown
                options={timezoneOptions}
                name="timezone"
                placeholder="Select timezone"
                onChangeFunction={handleInputChange}
                input={input}
                width="33.8rem"
              />
            </div>
          </div>
          <div className="email-container trip-types-row">
            <div className="user-modal-text">Trip Types</div>
            <div className="trip-types-fields">
              <div className="invite-text-input-container trip-types-default-container">
                {DEFAULT_TRIP_TYPES.map((tripType) => (
                  <div key={tripType} className="trip-type-default-pill">
                    {tripType}
                  </div>
                ))}
              </div>
              <div className="trip-types-extra-container">
                {(input.customTripTypes || []).map((tripType, index) => {
                  const fieldName = `customTripType-${index}`;
                  return (
                  <div className="trip-type-extra-field" key={`custom-trip-type-${index}`}>
                    <div className="trip-type-extra-input-wrap">
                      <TextInput
                        width="100%"
                        placeholder={`Example: Custom ${index + 1}`}
                        setInput={setInput}
                        input={{ [fieldName]: tripType }}
                        name={fieldName}
                        type="text"
                        error={Boolean(customTripTypeErrors[index])}
                        setError={() => {}}
                        errorMessage={customTripTypeErrors[index]}
                        onChangeFunction={(e) =>
                          handleCustomTripTypeChange(index, e.target.value)
                        }
                        keyUp={keyUp}
                        setKeyUp={setKeyUp}
                      />
                    </div>
                  </div>
                )})}
              </div>
              <div className="button-container-trip-types button-container-trip-types--add-only">
                <SecondaryButton
                  width="100%"
                  label="Add Trip Type"
                  onClickFunction={handleAddMore}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="button-container-delete">
          <SecondaryButton
            width="12rem"
            label="Cancel"
            imageIcon={false}
            onClickFunction={handleClose}
          />
          <PrimaryButton
            width="12rem"
            label="Save"
            onClickFunction={handleSave}
            disable={!isFormValid() || !hasChanges || customTripTypeErrors.some(Boolean)}
            loading={saveLoading}
          />
          <DeleteButton
            label="Delete"
            buttonWidth="12rem"
            buttonHeight="2.75rem"
            onClickFunction={() => setShowDeleteModal(true)}
          />
        </div>
      </Box>
    </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleConfirmDelete}
        itemName={{
          institutionName: institution.institutionName,
          institutionId: institution.institutionId,
          _id: institution._id,
        }}
        module="institution"
        loading={deleteLoading}
        stackLevel={1}
      />
    </>
  );
}
